/**
 * ============================================================================
 * ChatToolbar - CRITICAL UI COMPONENT
 * ============================================================================
 *
 * IMPORTANT: This sidebar toolbar is a CRUCIAL part of the app UI.
 *
 * RULES - DO NOT VIOLATE:
 * 1. This toolbar must ALWAYS be visible at all times
 * 2. NEVER hide this component on any screen size or device
 * 3. NEVER remove icons or make them conditional based on screen size
 * 4. On mobile: Use horizontal scrolling if needed, but NEVER hide
 * 5. All action icons must be accessible at all times
 *
 * If you need to make changes, ensure the toolbar remains fully visible
 * and all icons remain accessible on ALL devices and screen sizes.
 *
 * ============================================================================
 */
import React from 'react';
import {
  Bot,
  History,
  ListTodo,
  MessageCircle,
  Globe,
  ListOrdered,
  Settings,
  GitBranch,
  GitCommit,
  Shield,
  FileDown,
  Pencil,
  ArrowLeftFromLine,
  ArrowRightFromLine,
  Palette,
  Activity,
} from 'lucide-react';
import GithubIcon from './GithubIcon';
import { Button } from './Button';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import styles from './ChatToolbar.module.css';

interface ChatToolbarProps {
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  onFavorite?: () => void;
  isFavorited?: boolean;
  onDownload?: () => void;
  onImport?: () => void;
  onPermissions?: () => void;
  onSettings?: () => void;
  showDownload?: boolean;
  showImport?: boolean;
  showPermissions?: boolean;
  onAnnotate?: () => void;
  githubConnected?: boolean;
  onGithubConnect?: () => void;
  panelSide?: 'left' | 'right';
  onToggleSide?: () => void;
  isOwner?: boolean; // Whether the current user is the session owner
  className?: string;
  chatUnreadCount?: number; // Number of unread team chat messages
}

export const ChatToolbar: React.FC<ChatToolbarProps> = ({
  activeTab,
  onTabChange,
  onFavorite,
  isFavorited = false,
  onDownload,
  onImport,
  onPermissions,
  onSettings,
  showDownload = false,
  showImport = false,
  showPermissions = false,
  onAnnotate,
  className,
  githubConnected = false,
  onGithubConnect,
  panelSide = 'right',
  onToggleSide,
  isOwner = false,
  chatUnreadCount = 0,
}) => {
  const tabs = [
    { id: 'ai', icon: Bot, label: 'AI Chat' },
    { id: 'chat', icon: MessageCircle, label: 'Team Chat' },
    { id: 'activity', icon: Activity, label: 'AI Activity' },
    { id: 'checkpoints', icon: History, label: 'Checkpoints' },
    { id: 'todos', icon: ListTodo, label: 'Todos' },
    { id: 'queue', icon: ListOrdered, label: 'Queue' },
    { id: 'git', icon: GitCommit, label: 'Git Commit' },
    { id: 'sites', icon: Globe, label: 'Sites' },
    { id: 'themes', icon: Palette, label: 'Themes' },
    { id: 'settings', icon: Settings, label: 'Session Settings' },
  ];

  const actions = [
    {
      id: 'toggleSide',
      icon: panelSide === 'right' ? ArrowLeftFromLine : ArrowRightFromLine,
      label: panelSide === 'right' ? 'Move panel to left' : 'Move panel to right',
      onClick: onToggleSide,
      show: !!onToggleSide,
    },
    {
      id: 'download',
      icon: FileDown,
      label: 'Download all Code',
      onClick: onDownload,
      show: showDownload,
    },
    { id: 'import', icon: GitBranch, label: 'Import', onClick: onImport, show: showImport },
    {
      id: 'github',
      icon: (props: any) => <GithubIcon size={props.size || 18} connected={githubConnected} />,
      label: githubConnected ? 'GitHub Connected' : 'Connect GitHub',
      onClick: githubConnected ? onImport : onGithubConnect,
      show: isOwner && (!!onGithubConnect || githubConnected || showImport),
      active: githubConnected,
    },
    {
      id: 'permissions',
      icon: Shield,
      label: 'Permissions',
      onClick: onPermissions,
      show: showPermissions,
    },
    { id: 'annotate', icon: Pencil, label: 'Annotate', onClick: onAnnotate, show: !!onAnnotate },
    {
      id: 'settings',
      icon: Settings,
      label: 'Session Settings',
      onClick: onSettings,
      show: !!onSettings,
    },
  ];

  return (
    <>
      <div className={`${styles.toolbar} ${className || ''}`}>
        <div className={styles.tabsSection}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const showBadge = tab.id === 'chat' && chatUnreadCount > 0 && !isActive;
            return (
              <Tooltip key={tab.id}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onTabChange?.(tab.id)}
                    className={`${styles.tabButton} ${isActive ? styles.active : ''}`}
                    title={tab.label}
                  >
                    <div className={styles.iconWrapper}>
                      <Icon size={18} />
                      {showBadge && (
                        <span className={styles.notificationBadge}>
                          {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                        </span>
                      )}
                    </div>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {tab.label}
                  {showBadge && ` (${chatUnreadCount} new)`}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        <div className={styles.divider} />

        <div className={styles.actionsSection}>
          {actions
            .filter((action) => action.show)
            .map((action) => {
              const Icon = action.icon;
              return (
                <Tooltip key={action.id}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={action.onClick}
                      className={`${styles.actionButton} ${action.active ? styles.active : ''}`}
                      title={action.label}
                    >
                      {typeof Icon === 'function' ? (
                        (Icon as any)({ size: 18 })
                      ) : (
                        <Icon size={18} fill={action.active ? 'currentColor' : 'none'} />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">{action.label}</TooltipContent>
                </Tooltip>
              );
            })}
        </div>
      </div>
    </>
  );
};
